import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { TitleBar, MenuBar, Toolbar, StatusBar } from './Chrome';
import TreeView from './TreeView';
import ListView from './ListView';
import PropertiesDialog from './PropertiesDialog';
import ContextMenu from './ContextMenu';
import AboutDialog from './AboutDialog';
import WelcomeDialog from './WelcomeDialog';
import ContactDialog from './ContactDialog';
import { treeData, nodeContents, OBJECT_TYPES } from '../../mock/mockData';
import { searchAll } from './searchIndex';

// Build node id->node and id->parentId maps
const buildMaps = (root) => {
  const nodeMap = {};
  const parentMap = {};
  const walk = (node, parent) => {
    nodeMap[node.id] = node;
    if (parent) parentMap[node.id] = parent.id;
    if (node.children) node.children.forEach((c) => walk(c, node));
  };
  walk(root, null);
  return { nodeMap, parentMap };
};

const SEARCH_NODE_ID = '__search_results__';

const ADUCApp = () => {
  const { nodeMap, parentMap } = useMemo(() => buildMaps(treeData), []);
  const [selectedNodeId, setSelectedNodeId] = useState('domain');
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [history, setHistory] = useState(['domain']);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [propsOpen, setPropsOpen] = useState(false);
  const [propsItem, setPropsItem] = useState(null);
  const [findOpen, setFindOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searchQuery, setSearchQuery] = useState('');

  // Mobile mode: auto-detect + manual override
  const [viewMode, setViewMode] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 820 ? 'mobile' : 'desktop'));
  const [viewModeOverride, setViewModeOverride] = useState(false);
  useEffect(() => {
    const onResize = () => {
      if (viewModeOverride) return;
      setViewMode(window.innerWidth < 820 ? 'mobile' : 'desktop');
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [viewModeOverride]);

  // Mobile resizable split (vertical) - tree on top, list below
  const [splitPct, setSplitPct] = useState(38);

  const items = searchResults != null ? searchResults : (nodeContents[selectedNodeId] || []);
  const currentNode = searchResults != null
    ? { id: SEARCH_NODE_ID, name: `Search Results for "${searchQuery}"`, type: 'SAVED_QUERIES' }
    : nodeMap[selectedNodeId];

  const navigate = useCallback((nodeId) => {
    if (!nodeId || !nodeMap[nodeId]) return;
    setSearchResults(null);
    setSelectedNodeId(nodeId);
    setSelectedItemId(null);
    setHistory((prev) => {
      const next = prev.slice(0, historyIdx + 1);
      next.push(nodeId);
      return next;
    });
    setHistoryIdx((i) => i + 1);
  }, [nodeMap, historyIdx]);

  const onTreeSelect = (id) => {
    if (id === selectedNodeId && searchResults == null) return;
    navigate(id);
  };

  const onListDoubleClick = (item) => {
    // From search results: navigate to parent and select
    if (searchResults != null && item.parentId) {
      navigate(item.parentId);
      setTimeout(() => setSelectedItemId(item.id), 0);
      return;
    }
    if (['OU', 'CONTAINER', 'DOMAIN', 'BUILTIN', 'SAVED_QUERIES'].includes(item.type) && nodeContents[item.id]) {
      navigate(item.id);
    } else {
      openProperties(item);
    }
  };

  const openProperties = (item) => { setPropsItem(item); setPropsOpen(true); };
  const openSelectedProperties = () => {
    if (!selectedItemId) return;
    const it = items.find((i) => i.id === selectedItemId);
    if (it) openProperties(it);
  };

  const onBack = () => {
    if (historyIdx > 0) {
      const idx = historyIdx - 1;
      setHistoryIdx(idx);
      setSelectedNodeId(history[idx]);
      setSelectedItemId(null);
      setSearchResults(null);
    }
  };
  const onForward = () => {
    if (historyIdx < history.length - 1) {
      const idx = historyIdx + 1;
      setHistoryIdx(idx);
      setSelectedNodeId(history[idx]);
      setSelectedItemId(null);
      setSearchResults(null);
    }
  };
  const onUp = () => {
    if (searchResults != null) { setSearchResults(null); return; }
    const p = parentMap[selectedNodeId];
    if (p) navigate(p);
  };
  const onRefresh = () => {
    // User specifically wants the refresh icon to reload the page
    window.location.reload();
  };

  const performSearch = (q) => {
    if (!q || !q.trim()) return;
    const results = searchAll(q);
    setSearchResults(results);
    setSearchQuery(q);
    setSelectedItemId(null);
    setFindOpen(false);
  };

  const pathString = (() => {
    if (searchResults != null) return `Search Results for "${searchQuery}"`;
    const chain = [];
    let cur = selectedNodeId;
    while (cur) {
      chain.unshift(nodeMap[cur]?.name || '');
      cur = parentMap[cur];
    }
    return chain.join(' / ');
  })();

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (propsOpen || findOpen || aboutOpen || welcomeOpen || contactOpen) return;
      if (e.key === 'F5') { e.preventDefault(); onRefresh(); }
      else if (e.key === 'Enter' && (e.altKey || !e.shiftKey)) { if (selectedItemId) { e.preventDefault(); openSelectedProperties(); } }
      else if (e.key === 'Backspace') { if (parentMap[selectedNodeId] || searchResults != null) { e.preventDefault(); onUp(); } }
      else if (e.key === 'F3' || (e.ctrlKey && e.key.toLowerCase() === 'f')) { e.preventDefault(); setFindOpen(true); }
      else if (e.key === 'Escape') setCtx(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, selectedNodeId, propsOpen, findOpen, aboutOpen, welcomeOpen, contactOpen, searchResults]);

  const setMode = (mode) => {
    setViewMode(mode);
    setViewModeOverride(true);
  };

  // Build dropdown menu definitions for the menu bar
  const menuItems = {
    Action: [
      { label: 'Contact / Inquiry...', accel: '', onClick: () => setContactOpen(true) },
      { label: 'Return to Welcome page', onClick: () => setWelcomeOpen(true) },
      { separator: true },
      { label: 'Find...', accel: 'Ctrl+F', onClick: () => setFindOpen(true) },
      { separator: true },
      { label: 'Properties', accel: 'Alt+Enter', disabled: !selectedItemId, onClick: openSelectedProperties },
    ],
    View: [
      { label: 'Desktop View', checked: viewMode === 'desktop', onClick: () => setMode('desktop') },
      { label: 'Mobile View', checked: viewMode === 'mobile', onClick: () => setMode('mobile') },
      { separator: true },
      { label: 'Auto (detect screen size)', onClick: () => { setViewModeOverride(false); setViewMode(window.innerWidth < 820 ? 'mobile' : 'desktop'); } },
      { separator: true },
      { label: 'Refresh (reloads page)', accel: 'F5', onClick: onRefresh },
    ],
    Help: [
      { label: 'About Active Directory Users and Computers', onClick: () => setAboutOpen(true) },
    ],
  };

  // Right-click context menus
  const buildListCtx = (item) => {
    const isDrillable = ['OU', 'CONTAINER', 'DOMAIN', 'BUILTIN'].includes(item.type) && nodeContents[item.id];
    return [
      isDrillable && { label: 'Open', accel: 'Enter', onClick: () => navigate(item.id) },
      isDrillable && { separator: true },
      { label: 'Find...', accel: 'Ctrl+F', onClick: () => setFindOpen(true) },
      { separator: true },
      { label: 'Refresh', accel: 'F5', onClick: onRefresh },
      { separator: true },
      { label: 'Properties', accel: 'Alt+Enter', onClick: () => openProperties(item) },
    ].filter(Boolean);
  };
  const buildPaneCtx = () => [
    { label: 'Refresh', accel: 'F5', onClick: onRefresh },
    { separator: true },
    { label: 'Contact / Inquiry...', onClick: () => setContactOpen(true) },
    { label: 'Return to Welcome', onClick: () => setWelcomeOpen(true) },
  ];
  const buildTreeCtx = (nodeId) => {
    const node = nodeMap[nodeId];
    if (!node) return [];
    const canDrill = nodeContents[nodeId];
    return [
      canDrill && { label: 'Open', onClick: () => navigate(nodeId) },
      canDrill && { separator: true },
      { label: 'Find...', accel: 'Ctrl+F', onClick: () => setFindOpen(true) },
      { separator: true },
      { label: 'Refresh', accel: 'F5', onClick: onRefresh },
      { separator: true },
      { label: 'Properties', onClick: () => openProperties({ id: nodeId, name: node.name, type: node.type, description: node.description || '' }) },
    ].filter(Boolean);
  };
  const onListContext = (e, item) => {
    e.preventDefault();
    setSelectedItemId(item.id);
    setCtx({ x: e.clientX, y: e.clientY, items: buildListCtx(item) });
  };
  const onPaneContext = (e) => {
    e.preventDefault();
    setCtx({ x: e.clientX, y: e.clientY, items: buildPaneCtx() });
  };
  const onTreeContext = (e, nodeId) => {
    e.preventDefault();
    setCtx({ x: e.clientX, y: e.clientY, items: buildTreeCtx(nodeId) });
  };

  // Vertical split drag handler (mobile mode)
  const startVDrag = (e) => {
    const startY = e.clientY;
    const startPct = splitPct;
    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      const totalH = window.innerHeight - 90; // approximate non-pane chrome
      const newPct = Math.max(15, Math.min(85, startPct + (dy / totalH) * 100));
      setSplitPct(newPct);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const isMobile = viewMode === 'mobile';

  return (
    <div
      className="h-screen w-screen flex flex-col bg-[#F0F0F0] overflow-hidden"
      onContextMenu={(e) => e.preventDefault()}
    >
      <TitleBar />
      <MenuBar menuItems={menuItems} viewMode={viewMode} />
      <Toolbar
        onBack={onBack}
        onForward={onForward}
        onUp={onUp}
        onRefresh={onRefresh}
        onProperties={openSelectedProperties}
        onFind={() => setFindOpen(true)}
        canBack={historyIdx > 0}
        canForward={historyIdx < history.length - 1}
        canUp={!!parentMap[selectedNodeId] || searchResults != null}
        hasSelection={!!selectedItemId}
      />

      <div
        className="flex items-center h-[20px] bg-[#FFFFFF] border-b border-[#D0D0D0] px-[8px] text-[11px] text-[#555] truncate"
        style={{ fontFamily: 'Tahoma, Segoe UI, sans-serif' }}
      >
        <span className="truncate">{pathString}</span>
      </div>

      {/* Main split - direction depends on view mode */}
      {isMobile ? (
        <div className="flex flex-col flex-1 min-h-0 border-t border-[#A0A0A0]">
          <div
            className="border-b border-[#A0A0A0] bg-white overflow-hidden flex-shrink-0"
            style={{ height: `${splitPct}%`, minHeight: 100 }}
          >
            <PaneHeader title="Console Root" />
            <div className="h-[calc(100%-22px)]">
              <TreeView
                treeData={treeData}
                selectedId={selectedNodeId}
                onSelect={onTreeSelect}
                onContextMenu={onTreeContext}
              />
            </div>
          </div>
          {/* Horizontal drag bar (mobile) */}
          <div
            onMouseDown={startVDrag}
            className="h-[6px] bg-[#E0E0E0] hover:bg-[#C0C0C0] cursor-row-resize border-y border-[#A0A0A0] flex items-center justify-center flex-shrink-0"
          >
            <div className="w-[40px] h-[2px] bg-[#888]" />
          </div>
          <div
            id="list-view-container"
            className="flex-1 min-h-0 overflow-hidden"
            style={{ transition: 'opacity 120ms' }}
            onContextMenu={(e) => {
              if (e.target === e.currentTarget || !e.target.closest('[data-list-row]')) onPaneContext(e);
            }}
          >
            <PaneHeader title={currentNode?.name || ''} />
            <div className="h-[calc(100%-22px)]">
              <ListView
                items={items}
                selectedId={selectedItemId}
                onSelect={setSelectedItemId}
                onDoubleClick={onListDoubleClick}
                typeLabels={OBJECT_TYPES}
                onContextMenu={onListContext}
                onEmptyContextMenu={onPaneContext}
                showLocationCol={searchResults != null}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 min-h-0 border-t border-[#A0A0A0]">
          <div className="w-[280px] min-w-[200px] border-r border-[#A0A0A0] bg-white overflow-hidden">
            <PaneHeader title="Console Root" />
            <div className="h-[calc(100%-22px)]">
              <TreeView
                treeData={treeData}
                selectedId={selectedNodeId}
                onSelect={onTreeSelect}
                onContextMenu={onTreeContext}
              />
            </div>
          </div>
          <div
            id="list-view-container"
            className="flex-1 min-w-0 overflow-hidden"
            style={{ transition: 'opacity 120ms' }}
            onContextMenu={(e) => {
              if (e.target === e.currentTarget || !e.target.closest('[data-list-row]')) onPaneContext(e);
            }}
          >
            <PaneHeader title={currentNode?.name || ''} />
            <div className="h-[calc(100%-22px)]">
              <ListView
                items={items}
                selectedId={selectedItemId}
                onSelect={setSelectedItemId}
                onDoubleClick={onListDoubleClick}
                typeLabels={OBJECT_TYPES}
                onContextMenu={onListContext}
                onEmptyContextMenu={onPaneContext}
                showLocationCol={searchResults != null}
              />
            </div>
          </div>
        </div>
      )}

      <StatusBar count={items.length} selectedCount={selectedItemId ? 1 : 0} />

      <PropertiesDialog open={propsOpen} item={propsItem} onClose={() => setPropsOpen(false)} typeLabels={OBJECT_TYPES} />
      <FindDialog open={findOpen} onClose={() => setFindOpen(false)} onSearch={performSearch} />
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <WelcomeDialog
        open={welcomeOpen}
        onClose={() => setWelcomeOpen(false)}
        onExplore={() => setWelcomeOpen(false)}
      />
      <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />
      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctx.items} onClose={() => setCtx(null)} />}
    </div>
  );
};

const PaneHeader = ({ title }) => (
  <div
    className="flex items-center h-[22px] bg-gradient-to-b from-[#FBFBFB] to-[#E8E8E8] border-b border-[#C0C0C0] px-[8px] text-[11px] text-black font-semibold truncate"
    style={{ fontFamily: 'Tahoma, Segoe UI, sans-serif' }}
  >
    <span className="truncate">{title}</span>
  </div>
);

const FindDialog = ({ open, onClose, onSearch }) => {
  const [scope, setScope] = useState('Users, Contacts, and Groups');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');

  if (!open) return null;
  const submit = () => {
    const q = (name || desc || '').trim();
    if (q) onSearch(q);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30">
      <div
        className="bg-[#F0F0F0] border border-[#808080] shadow-2xl"
        style={{ width: 480, fontFamily: 'Tahoma, Segoe UI, sans-serif' }}
      >
        <div className="flex items-center justify-between h-[26px] bg-[#2B579A] text-white px-[8px]">
          <span className="text-[12px]">Find Users, Contacts, and Groups</span>
          <button onClick={onClose} className="w-[28px] h-[26px] hover:bg-[#E81123] flex items-center justify-center">
            <svg width="10" height="10" viewBox="0 0 10 10">
              <line x1="1" y1="1" x2="9" y2="9" stroke="#FFF" strokeWidth="1.2" />
              <line x1="9" y1="1" x2="1" y2="9" stroke="#FFF" strokeWidth="1.2" />
            </svg>
          </button>
        </div>
        <div className="p-[12px] text-[12px]">
          <div className="flex items-center mb-[8px]">
            <div className="w-[60px]">Find:</div>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="border border-[#8B8B8B] h-[22px] text-[12px] flex-1"
            >
              <option>Users, Contacts, and Groups</option>
              <option>Computers</option>
              <option>Organizational Units</option>
              <option>Custom Search (everything)</option>
            </select>
          </div>
          <div className="flex items-center mb-[10px]">
            <div className="w-[60px]">In:</div>
            <div className="border border-[#8B8B8B] h-[22px] bg-white flex-1 flex items-center px-[4px]">philjustus.local</div>
          </div>
          <div className="border border-[#C0C0C0] p-[10px] bg-white">
            <div className="flex items-center mb-[6px]">
              <div className="w-[80px]">Name:</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                className="border border-[#8B8B8B] h-[22px] flex-1 px-[4px] text-[12px] focus:outline-none focus:border-[#2B579A]"
                placeholder="e.g., CISSP, Splunk, ISSM"
                autoFocus
              />
            </div>
            <div className="flex items-center">
              <div className="w-[80px]">Description:</div>
              <input
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                className="border border-[#8B8B8B] h-[22px] flex-1 px-[4px] text-[12px] focus:outline-none focus:border-[#2B579A]"
              />
            </div>
          </div>
          <div className="flex justify-end gap-[6px] mt-[12px]">
            <button
              onClick={submit}
              className="min-w-[80px] h-[23px] text-[12px] border border-[#2B579A] bg-[#DCE9F9] hover:bg-[#C1D7F0] active:bg-[#B0CEEC]"
            >
              Find Now
            </button>
            <button
              onClick={() => { setName(''); setDesc(''); }}
              className="min-w-[80px] h-[23px] text-[12px] border border-[#707070] bg-[#F0F0F0] hover:border-[#2B579A] hover:bg-[#DCE9F9]"
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="min-w-[80px] h-[23px] text-[12px] border border-[#707070] bg-[#F0F0F0] hover:border-[#2B579A] hover:bg-[#DCE9F9]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ADUCApp;
